import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusCircleIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleSolid } from "@heroicons/react/24/solid";

/* ════════════════════════════════════════════════════════════════════════════ */
const CheckInCheckOutCard = ({
  today,
  error,
  isLoading,
  setShowLeaveModal,
  employeeId,
  employeeName,
  SERVER_URL,
  isOnLeaveToday,
  leaveTypeId,
  leaveStatus,
  onAttendanceUpdated,
}) => {
  const [checkInStatus, setCheckInStatus] = useState(false);
  const [checkOutStatus, setCheckOutStatus] = useState(false);
  const [timeStamp, setTimeStamp] = useState({ checkInTime: "", checkOutTime: "" });
  const [submitting, setSubmitting] = useState(null); // 'checkIn' | 'checkOut' | null

  const isLeaveBlockingAttendance =
    isOnLeaveToday && leaveStatus === "approved" && leaveTypeId !== 5;

  useEffect(() => {
    fetchCheckInStatus(employeeId, employeeName);
  }, [employeeId, employeeName]);

  const formatTime = (timeString) => {
    if (!timeString) return "—";
    const [hours, minutes] = timeString.split(":");
    return `${hours}:${minutes}`;
  };

  const getCurrentTime = () => new Date().toTimeString().slice(0, 8);

  const submitAttendance = async (type) => {
  setSubmitting(type);

  try {
    let latitude = null, longitude = null, location_name = null;

    // 🔴 FORCE LOCATION REQUIRED
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        reject,
        { timeout: 5000 }
      );
    }).catch(() => null);

    // ❌ If no location → STOP
    if (!position) {
      alert("❌ Location is required to mark attendance!");
      setSubmitting(null);
      return;
    }

    latitude = position.coords.latitude;
    longitude = position.coords.longitude;

    // Reverse geocode
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
      );
      const data = await res.json();
      location_name = data.display_name || null;
    } catch {}

    // ❌ Extra safety check
    if (!latitude || !longitude) {
      alert("❌ Location not detected. Please enable GPS.");
      setSubmitting(null);
      return;
    }

    // 🔥 API CALL
    const response = await axios.post(
      `${SERVER_URL}/DataInsert/submitAttendance.php`,
      {
        employee_id: employeeId,
        type,
        latitude,
        longitude,
        location_name,
      }
    );

    // ✅ SUCCESS / FAILURE POPUP
    if (response.data.status) {
      alert(`✅ ${type === "checkIn" ? "Check-in" : "Check-out"} successful!`);

      if (type === "checkIn") {
        setCheckInStatus(true);
        setTimeStamp((prev) => ({
          ...prev,
          checkInTime: response.data.saved_time,
        }));
      }

      if (type === "checkOut") {
        setCheckOutStatus(true);
        setTimeStamp((prev) => ({
          ...prev,
          checkOutTime: response.data.saved_time,
        }));
      }
    } else {
      alert(`❌ ${response.data.message}`);
    }

    window.dispatchEvent(new Event("attendance-updated"));
    fetchCheckInStatus(employeeId, employeeName);

    if (onAttendanceUpdated) onAttendanceUpdated();

  } catch (error) {
    console.error("Attendance error:", error);
    alert("❌ Something went wrong!");
  } finally {
    setSubmitting(null);
  }
};

  const fetchCheckInStatus = async (employeeId, employeeName) => {
    try {
      const res = await axios.post(`${SERVER_URL}/DataFetch/checkInStatus.php`, {
        employee_id: employeeId || null,
        employee_name: employeeName || null,
      });
      if (res.data.status == true) {
        setCheckOutStatus(res.data.checkout);
        setCheckInStatus(res.data.checkin);
        setTimeStamp({
          checkInTime: res.data.checkInTime || "",
          checkOutTime: res.data.checkOutTime || "",
        });
      }
    } catch (error) {
      console.error("Error fetching check-in status:", error);
    }
  };

  const hasCheckedIn = checkInStatus && timeStamp.checkInTime;
  const hasCheckedOut = checkOutStatus && timeStamp.checkOutTime;

  /* ════════════════════════════════ JSX ════════════════════════════════ */
  return (
    <>
      <style>{`
        .att-card { background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%); }
        .time-pill { background: linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%); }
        .checkin-btn { background: linear-gradient(135deg, #10b981 0%, #059669 100%); box-shadow: 0 4px 14px rgba(16,185,129,0.3); }
        .checkin-btn:hover:not(:disabled) { box-shadow: 0 6px 20px rgba(16,185,129,0.4); transform: translateY(-1px); }
        .checkout-btn { background: linear-gradient(135deg, #f43f5e 0%, #e11d48 100%); box-shadow: 0 4px 14px rgba(244,63,94,0.3); }
        .checkout-btn:hover:not(:disabled) { box-shadow: 0 6px 20px rgba(244,63,94,0.4); transform: translateY(-1px); }
        .leave-btn { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); box-shadow: 0 4px 14px rgba(245,158,11,0.3); }
        .leave-btn:hover { box-shadow: 0 6px 20px rgba(245,158,11,0.4); transform: translateY(-1px); }
        .att-btn { transition: all 0.2s ease; }
      `}</style>

      <div className="att-card rounded-2xl p-5 border border-indigo-50 shadow-sm shadow-indigo-100">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
              <CalendarDaysIcon className="w-5 h-5 text-indigo-500" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-800 leading-tight">Today's Attendance</h2>
              <p className="text-xs text-slate-400">{today}</p>
            </div>
          </div>

          {/* Status pill */}
          {isLeaveBlockingAttendance ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-600 border border-amber-200">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
              On Leave
            </span>
          ) : hasCheckedIn && hasCheckedOut ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200">
              <CheckCircleSolid className="w-3.5 h-3.5" />
              Complete
            </span>
          ) : hasCheckedIn ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-200">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
              Checked In
            </span>
          ) : null}
        </div>

        {/* Time Display Row */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="time-pill rounded-xl p-3 border border-indigo-100">
            <div className="flex items-center gap-1.5 mb-1">
              <ClockIcon className="w-3.5 h-3.5 text-emerald-500" />
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Check In</span>
            </div>
            <span className={`text-xl font-bold font-mono tracking-tight ${hasCheckedIn ? "text-emerald-600" : "text-slate-300"}`}>
              {formatTime(timeStamp.checkInTime)}
            </span>
          </div>

          <div className="time-pill rounded-xl p-3 border border-indigo-100">
            <div className="flex items-center gap-1.5 mb-1">
              <ClockIcon className="w-3.5 h-3.5 text-rose-400" />
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Check Out</span>
            </div>
            <span className={`text-xl font-bold font-mono tracking-tight ${hasCheckedOut ? "text-rose-500" : "text-slate-300"}`}>
              {formatTime(timeStamp.checkOutTime)}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
          <button
            onClick={() => setShowLeaveModal(true)}
            className="leave-btn att-btn flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white"
          >
            <PlusCircleIcon className="w-4 h-4 flex-shrink-0" />
            <span>Apply Leave</span>
          </button>

          <button
            onClick={() => submitAttendance("checkIn")}
            disabled={isLeaveBlockingAttendance || submitting === "checkIn"}
            className="checkin-btn att-btn flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
          >
            {submitting === "checkIn" ? (
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            ) : (
              <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
            )}
            <span>{submitting === "checkIn" ? "Checking In…" : "Check In"}</span>
          </button>

          <button
            onClick={() => submitAttendance("checkOut")}
            disabled={isLeaveBlockingAttendance || submitting === "checkOut"}
            className="checkout-btn att-btn flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
          >
            {submitting === "checkOut" ? (
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            ) : (
              <XCircleIcon className="w-4 h-4 flex-shrink-0" />
            )}
            <span>{submitting === "checkOut" ? "Checking Out…" : "Check Out"}</span>
          </button>
        </div>

        {error && (
          <div className="mt-3 px-4 py-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-600 text-sm font-medium">
            {error}
          </div>
        )}
      </div>
    </>
  );
};

export default CheckInCheckOutCard;